import i18next from "i18next";
import english from "./interface.en.json" with { type: "json" };
import russian from "./interface.ru.json" with { type: "json" };
import fields from "./fields.ru.json" with { type: "json" };

export const isRussian = () => (i18next.resolvedLanguage || i18next.language || "en").startsWith("ru");
export const appLocale = (englishLocale = "en-GB") => isRussian() ? "ru-RU" : englishLocale;
/** Retain the browser's previous default formatting in English mode. */
export const browserLocale = () => isRussian() ? "ru-RU" : undefined;
const normalize = (text: string) => text.trim().replace(/[_-]+/g, " ").replace(/\s+/g, " ").toLowerCase();
const labels = new Map<string, string>();
const exactLabels = new Map<string, string>();
const originals = new Map<string, string>();
for (const [key, text] of Object.entries(english)) {
	const translated = russian[key as keyof typeof russian];
	if (!labels.has(normalize(text))) labels.set(normalize(text), translated);
	if (!exactLabels.has(text)) exactLabels.set(text, translated);
	if (text !== translated && !/[а-яё]/i.test(text)) originals.set(translated, text);
}

/** Only use on presentation metadata, never on merchant names, payloads or identifiers. */
export function displayText(text: string): string {
	if (!isRussian()) return /[а-яё]/i.test(text) ? originals.get(text) ?? text : text;
	return exactLabels.get(text) ?? labels.get(normalize(text)) ?? text;
}

/** Reverse a translated presentation label when a legacy renderer indexes metadata by that label. */
export const englishText = (text: string) => originals.get(text) ?? text;

/** Keeps the original English header unchanged, including its case and underscores. */
export function displayField(field: string, englishLabel = field): string {
	if (!isRussian()) return /[а-яё]/i.test(englishLabel) ? originals.get(englishLabel) ?? englishLabel : englishLabel;
	if (/[а-яё]/i.test(englishLabel)) return englishLabel;
	if (normalize(field) !== normalize(englishLabel)) {
		const explicitLabel = displayText(englishLabel);
		if (explicitLabel !== englishLabel) return explicitLabel;
	}
	const key = field.trim().replace(/[ -]+/g, "_").toLowerCase();
	return fields[key as keyof typeof fields] ?? displayText(englishLabel);
}

/** Preserve option values submitted to the API; localize only their visible names. */
export function displayOptions(values: unknown): any[] {
	if (!Array.isArray(values)) return [];
	return values.map(value => {
		if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
			return { value, name: displayText(typeof value === "boolean" ? value ? "YES" : "NO" : String(value)) };
		}
		if (!value || typeof value !== "object") return value;
		if ("id" in value || "client_id" in value || "merchant_id" in value) return value;
		const labelKey = ["name", "label", "title", "displayName"].find(key => typeof value[key] === "string");
		return labelKey ? { ...value, [labelKey]: displayText(value[labelKey]) } : value;
	});
}

export function countryName(code: string, fallback: string): string {
	if (!isRussian() || !/^[a-z]{2}$/i.test(code)) return fallback;
	try { return new Intl.DisplayNames(["ru"], { type: "region", fallback: "none" }).of(code.toUpperCase()) ?? fallback; }
	catch { return fallback; }
}
